import { Injectable } from '@angular/core';
import { AnalyticsService } from '@com-delta-idp/mnx-utils';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { combineLatest, of } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { StateFacade as OverviewFacade } from '../../facades/state.facade';
import { ErrorConstant, PLACE_HOLDER, SECTION_NAME } from '../../models';
import { DataLayerAnalyticsService } from '../../services/analytics/data-layer.service';
import { SkyClubTrackerService } from '../../services/api/skyclub-tracker.service';
import * as OverviewActions from '../actions/api.actions';

@Injectable()
export class AmexSkyClubTrackerEffects {
  loadAmexSkyClubTrackerData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OverviewActions.loadAmexSkyClubTracker),
      switchMap(() =>
        combineLatest([
          this.overview.flags$.pipe(
            filter((flags) => flags !== undefined),
            take(1)
          ),
          this.overview.skyclubRequest$.pipe(
            filter((skyClubRequest) => skyClubRequest !== undefined),
            take(1)
          ),
          this.overview.serviceConfiguration$.pipe(
            filter(
              (serviceConfiguration) => !!serviceConfiguration?.carrierCode
            ),
            take(1)
          ),
          this.overview.aemErrorMessages$.pipe(
            filter((aemErrorMessages) => aemErrorMessages !== undefined),
            take(1)
          )
        ]).pipe(
          switchMap(
            ([
              flags,
              skyClubRequest,
              serviceConfiguration,
              aemErrorMessages
            ]) => {
              if (
                flags &&
                flags.visitSpend &&
                skyClubRequest.affiliationList.length > 0
              ) {
                return this.skyclub
                  .getSkyClubTrackerVisit(serviceConfiguration, skyClubRequest)
                  .pipe(
                    map((content) =>
                      OverviewActions.loadAmexSkyClubTrackerSuccess({
                        amexSkyClubTracker: {
                          id: 'amex-skyclub-tracker-content',
                          amexSkyClubTrackerData: content
                        }
                      })
                    ),
                    catchError((error) => {
                      const message = aemErrorMessages.find((item) => {
                        return item.component === 'skyclub-tracker';
                      });
                      this.dataLayer.setErrorData(
                        error,
                        message.errorMessageDescription,
                        ErrorConstant.EVENT_NAME.replace(
                          PLACE_HOLDER,
                          SECTION_NAME.SKY_CLUB_TRACKER
                        ),
                        ErrorConstant.TYPE.replace(
                          PLACE_HOLDER,
                          SECTION_NAME.SKY_CLUB_TRACKER
                        )
                      );
                      this.analyticsService.dispatchCustomEvent<string>(
                        'error-triggered:SkyClubTracker',
                        ''
                      );
                      return of(
                        OverviewActions.loadAmexSkyClubTrackerFailure({
                          error: {
                            id: 'amex-skyclub-tracker-content-error',
                            amexSkyClubTrackerDataError: 'amex-skyclub-tracker'
                          }
                        })
                      );
                    })
                  );
              } else {
                return of(
                  OverviewActions.loadAmexSkyClubTrackerSuccess({
                    amexSkyClubTracker: {
                      id: 'amex-skyclub-tracker-content',
                      amexSkyClubTrackerData: { visitRecord: [] }
                    }
                  })
                );
              }
            }
          )
        )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly skyclub: SkyClubTrackerService,
    private readonly overview: OverviewFacade,
    private analyticsService: AnalyticsService,
    protected dataLayer: DataLayerAnalyticsService
  ) {}
}
